import React, { ReactNode } from 'react';
import { NavigationCounter } from '../atoms/NavigationCounter';

export interface SidebarItemProps extends Omit<React.HTMLAttributes<HTMLElement>, 'as'> {
  icon?: ReactNode;
  label: string;
  isActive?: boolean;
  collapsed?: boolean;
  onClick?: () => void;
  as?: React.ElementType;
  to?: string;
  className?: string;
  children?: ReactNode;
  count?: number;
  countLabel?: string;
}

const stateClasses = {
  idle: 'text-slate-400 [&_svg]:text-slate-500 bg-transparent',
  hover: 'hover:text-white hover:[&_svg]:text-white hover:bg-white/5 hover:translate-x-1',
  active: `
    text-white [&_svg]:text-white
    bg-white/10 border border-white/20
    shadow-[0_0_15px_rgba(59,130,246,0.3)]
    relative
    before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2
    before:w-[3px] before:h-5 before:bg-brand-500 before:rounded-r-full
  `,
};

export const SidebarItem = React.forwardRef<HTMLElement, SidebarItemProps>(
  (
    {
      icon,
      label,
      isActive = false,
      collapsed = false,
      onClick,
      as: Component = 'button',
      to,
      className = '',
      children,
      count,
      countLabel = label,
      ...props
    },
    ref
  ) => {
    const baseClasses = `
      flex items-center gap-3 py-3 px-4
      rounded-lg
      transition-all duration-200 ease-out
      text-sm font-medium cursor-pointer w-full text-left
    `;

    const stateStyles = isActive
      ? stateClasses.active
      : `${stateClasses.idle} ${stateClasses.hover}`;

    const classes = `${baseClasses} ${stateStyles} ${className}`;

    return (
      <Component
        ref={ref}
        className={`${classes} relative`}
        onClick={onClick}
        to={to}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {collapsed ? <span className="sr-only">{label}</span> : <span className="min-w-0 truncate">{label}</span>}
        {typeof count === 'number' && <NavigationCounter count={count} label={countLabel} collapsed={collapsed} />}
        {children}
      </Component>
    );
  }
);

SidebarItem.displayName = 'SidebarItem';
